/*
	Teléfono de Ventas dentro del navegador (reemplazo de VozMia). Se registra como extensión de la asesora en la central
	propia (Asterisk en sip.washingtonacademy.com) por WSS, con JsSIP (public/vendor, se carga solo al encender el teléfono).
	Un solo teléfono por pestaña: es un objeto único fuera de React; las pantallas se suscriben a sus cambios.
	Cada llamada lleva X-WA-Call (liga la grabación con la bitácora) y X-WA-Prospect.
*/

export interface PbxCreds { extension: string; password: string; name: string; domain: string; wss: string; iceServers: any[]; enabled: boolean; }

export type EstadoTel = "apagado" | "conectando" | "listo" | "error";

export interface Llamada {
	callId: string;
	direction: "out" | "in";
	number: string;
	name: string | null;
	prospectId: number | null;
	status: "marcando" | "sonando" | "entrante" | "en_llamada" | "terminada";
	startedAt: number | null;      // cuando contestaron (ms)
	muted: boolean;
	held: boolean;
	consult: { number: string; status: "marcando" | "en_llamada" } | null;   // transferencia con aviso
}

export interface FinLlamada { callId: string; prospectId: number | null; number: string; direction: "out" | "in"; durationSec: number; answered: boolean; cause: string | null; campaignContactId?: number | null; }

type Escucha = () => void;
type EscuchaFin = (f: FinLlamada) => void;

const SCRIPT = "/vendor/jssip-3.10.1.min.js";

const cargarJsSIP = (): Promise<any> => new Promise((resolve, reject) => {
	const w = window as any;
	if (w.JsSIP) return resolve(w.JsSIP);
	const s = document.createElement("script");
	s.src = SCRIPT;
	s.async = true;
	s.onload = () => (w.JsSIP ? resolve(w.JsSIP) : reject(new Error("No cargó el teléfono")));
	s.onerror = () => reject(new Error("No cargó el teléfono"));
	document.head.appendChild(s);
});

const uuid = (): string => {
	const c: any = (window as any).crypto;
	if (c?.randomUUID) return c.randomUUID();
	return "c" + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
};

/** "+504 3327-7716" → "50433277716" (lo que entiende el plan de marcado). */
export const aMarcable = (e164: string): string => (e164 || "").replace(/\D/g, "");

class Telefono {
	estado: EstadoTel = "apagado";
	error: string | null = null;
	creds: PbxCreds | null = null;
	llamada: Llamada | null = null;
	private ua: any = null;
	private session: any = null;
	private consultSession: any = null;
	private audio: HTMLAudioElement | null = null;
	private escuchas: Set<Escucha> = new Set();
	private finales: Set<EscuchaFin> = new Set();
	private campaignContactId: number | null = null;

	suscribir(fn: Escucha): () => void { this.escuchas.add(fn); return () => { this.escuchas.delete(fn); }; }
	alTerminar(fn: EscuchaFin): () => void { this.finales.add(fn); return () => { this.finales.delete(fn); }; }
	private avisar() { this.escuchas.forEach((f) => { try { f(); } catch (e) { /* una pantalla rota no apaga el teléfono */ } }); }

	get listo(): boolean { return this.estado === "listo"; }
	get ocupado(): boolean { return !!this.llamada && this.llamada.status !== "terminada"; }

	/** Encender: pide el micrófono (necesita un clic de la asesora) y registra la extensión. */
	async conectar(creds: PbxCreds): Promise<void> {
		if (this.ua && (this.estado === "listo" || this.estado === "conectando")) return;
		this.creds = creds;
		this.estado = "conectando"; this.error = null; this.avisar();
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			stream.getTracks().forEach((t) => t.stop());   // solo para pedir permiso una vez
		} catch (e) {
			this.estado = "error"; this.error = "El navegador no dio permiso al micrófono."; this.avisar();
			return;
		}
		let JsSIP: any;
		try { JsSIP = await cargarJsSIP(); } catch (e) { this.estado = "error"; this.error = "No cargó el teléfono. Recarga la página."; this.avisar(); return; }
		this.audio = this.audio || Object.assign(document.createElement("audio"), { autoplay: true });
		const socket = new JsSIP.WebSocketInterface(creds.wss);
		this.ua = new JsSIP.UA({
			sockets: [socket], uri: `sip:${creds.extension}@${creds.domain}`, password: creds.password,
			display_name: creds.name, register: true, session_timers: false, connection_recovery_min_interval: 2, connection_recovery_max_interval: 30,
		});
		this.ua.on("registered", () => { this.estado = "listo"; this.error = null; this.avisar(); });
		this.ua.on("unregistered", () => { if (this.estado === "listo") { this.estado = "conectando"; this.avisar(); } });
		this.ua.on("registrationFailed", (e: any) => { this.estado = "error"; this.error = "La central no aceptó la extensión (" + (e?.cause || "sin respuesta") + ")."; this.avisar(); });
		this.ua.on("disconnected", () => { if (this.estado !== "apagado") { this.estado = "conectando"; this.avisar(); } });
		this.ua.on("newRTCSession", (d: any) => this.nuevaSesion(d));
		this.ua.start();
	}

	apagar() {
		try { this.ua?.stop(); } catch (e) { /* ya estaba cerrado */ }
		this.ua = null; this.estado = "apagado"; this.avisar();
	}

	private opciones() {
		return { mediaConstraints: { audio: true, video: false }, pcConfig: { iceServers: this.creds?.iceServers || [] }, rtcOfferConstraints: { offerToReceiveAudio: true } };
	}

	private conectarAudio(session: any) {
		const pc: RTCPeerConnection | undefined = session.connection;
		if (!pc || !this.audio) return;
		const poner = () => {
			const recv = pc.getReceivers().map((r) => r.track).filter(Boolean) as MediaStreamTrack[];
			if (recv.length && this.audio) { this.audio.srcObject = new MediaStream(recv); this.audio.play().catch(() => { /* lo reanuda el siguiente clic */ }); }
		};
		pc.addEventListener("track", poner);
		poner();
	}

	private sonar(on: boolean) {
		if (on) {
			// Timbre sintético (sin archivos): dos tonos cortos repetidos.
			try {
				const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
				const ctx = new Ctx();
				const o = ctx.createOscillator(); const g = ctx.createGain();
				o.frequency.value = 440; o.connect(g); g.connect(ctx.destination); g.gain.value = 0;
				o.start();
				const t = setInterval(() => { g.gain.setValueAtTime(0.08, ctx.currentTime); g.gain.setValueAtTime(0, ctx.currentTime + 0.4); }, 1500);
				(this as any)._timbre = { ctx, t };
			} catch (e) { /* sin timbre: queda el aviso en pantalla */ }
		} else if ((this as any)._timbre) {
			clearInterval((this as any)._timbre.t); (this as any)._timbre.ctx.close?.(); (this as any)._timbre = null;
		}
	}

	private nuevaSesion(d: any) {
		const s = d.session;
		if (d.originator === "remote") {
			if (this.ocupado) { s.terminate({ status_code: 486 }); return; }   // ocupada: la central la manda a otra del equipo
			const num = String(s.remote_identity?.uri?.user || "");
			this.session = s;
			this.llamada = { callId: s.request?.getHeader?.("X-WA-Call") || uuid(), direction: "in", number: num, name: s.remote_identity?.display_name || null,
				prospectId: null, status: "entrante", startedAt: null, muted: false, held: false, consult: null };
			this.campaignContactId = null;
			this.sonar(true);
			this.engancharSesion(s);
			this.avisar();
		}
	}

	private engancharSesion(s: any) {
		s.on("peerconnection", () => this.conectarAudio(s));
		s.on("progress", () => { if (this.llamada && this.llamada.direction === "out") { this.llamada.status = "sonando"; this.avisar(); } });
		s.on("accepted", () => { if (this.llamada) { this.llamada.status = "en_llamada"; this.llamada.startedAt = Date.now(); this.sonar(false); this.avisar(); } });
		s.on("confirmed", () => this.conectarAudio(s));
		s.on("hold", () => { if (this.llamada) { this.llamada.held = true; this.avisar(); } });
		s.on("unhold", () => { if (this.llamada) { this.llamada.held = false; this.avisar(); } });
		const fin = (e: any) => this.terminar(s, e?.cause || null);
		s.on("ended", fin);
		s.on("failed", fin);
	}

	private terminar(s: any, cause: string | null) {
		if (s !== this.session || !this.llamada) return;
		this.sonar(false);
		const l = this.llamada;
		const f: FinLlamada = { callId: l.callId, prospectId: l.prospectId, number: l.number, direction: l.direction,
			durationSec: l.startedAt ? Math.round((Date.now() - l.startedAt) / 1000) : 0, answered: !!l.startedAt, cause, campaignContactId: this.campaignContactId };
		this.llamada = { ...l, status: "terminada" };
		this.session = null;
		if (this.consultSession) { try { this.consultSession.terminate(); } catch (e) { /* ya colgó */ } this.consultSession = null; }
		this.avisar();
		this.finales.forEach((fn) => { try { fn(f); } catch (e) { /* sigue con las demás */ } });
		setTimeout(() => { if (this.llamada?.status === "terminada") { this.llamada = null; this.avisar(); } }, 400);
	}

	/** Marcar. number = lo marcable (50433277716) o una extensión. */
	llamar(number: string, info: { prospectId?: number | null; name?: string | null; campaignContactId?: number | null } = {}): string | null {
		if (!this.ua || !this.listo || this.ocupado || !this.creds) return null;
		const callId = uuid();
		const headers = ["X-WA-Call: " + callId];
		if (info.prospectId) headers.push("X-WA-Prospect: " + info.prospectId);
		this.llamada = { callId, direction: "out", number, name: info.name || null, prospectId: info.prospectId || null, status: "marcando",
			startedAt: null, muted: false, held: false, consult: null };
		this.campaignContactId = info.campaignContactId || null;
		const s = this.ua.call(`sip:${number}@${this.creds.domain}`, { ...this.opciones(), extraHeaders: headers });
		this.session = s;
		this.engancharSesion(s);
		this.avisar();
		return callId;
	}

	contestar() {
		if (!this.session || this.llamada?.status !== "entrante") return;
		this.sonar(false);
		this.session.answer(this.opciones());
	}

	/** Pegar la persona que llama a su ficha (al abrirla desde el aviso de llamada entrante). */
	ligar(prospectId: number, name: string | null) {
		if (this.llamada) { this.llamada = { ...this.llamada, prospectId, name: name || this.llamada.name }; this.avisar(); }
	}

	colgar() {
		if (this.consultSession) { try { this.consultSession.terminate(); } catch (e) { /* ya */ } this.consultSession = null; }
		try { this.session?.terminate(); } catch (e) { /* ya colgó */ }
	}

	silenciar() {
		if (!this.session || !this.llamada) return;
		if (this.llamada.muted) this.session.unmute({ audio: true }); else this.session.mute({ audio: true });
		this.llamada = { ...this.llamada, muted: !this.llamada.muted }; this.avisar();
	}

	espera() {
		if (!this.session || !this.llamada) return;
		if (this.llamada.held) this.session.unhold(); else this.session.hold();
	}

	tecla(d: string) { try { this.session?.sendDTMF(d, { duration: 120, interToneGap: 70 }); } catch (e) { /* sin llamada */ } }

	/** Transferencia directa: el cliente pasa a esa extensión (o equipo 7xx) y esta asesora queda libre. */
	transferir(destino: string) {
		if (!this.session || !this.creds) return;
		this.session.refer(`sip:${destino}@${this.creds.domain}`);
	}

	/** Con aviso: el cliente queda en espera, se llama a la compañera; al completar, el cliente pasa con ella. */
	consultar(destino: string) {
		if (!this.session || !this.creds || !this.llamada || this.consultSession) return;
		if (!this.llamada.held) this.session.hold();
		const c = this.ua.call(`sip:${destino}@${this.creds.domain}`, this.opciones());
		this.consultSession = c;
		this.llamada = { ...this.llamada, consult: { number: destino, status: "marcando" } }; this.avisar();
		c.on("peerconnection", () => this.conectarAudio(c));
		c.on("accepted", () => { if (this.llamada?.consult) { this.llamada = { ...this.llamada, consult: { ...this.llamada.consult, status: "en_llamada" } }; this.avisar(); } });
		const fin = () => {
			if (this.consultSession !== c) return;
			this.consultSession = null;
			if (this.llamada) { this.llamada = { ...this.llamada, consult: null }; this.avisar(); }
			if (this.session) this.conectarAudio(this.session);
		};
		c.on("ended", fin); c.on("failed", fin);
	}

	completarTransferencia() {
		if (!this.session || !this.consultSession || !this.creds || !this.llamada?.consult) return;
		this.session.refer(`sip:${this.llamada.consult.number}@${this.creds.domain}`, { replaces: this.consultSession });
	}

	cancelarConsulta() {
		if (this.consultSession) { try { this.consultSession.terminate(); } catch (e) { /* ya */ } }
		if (this.session && this.llamada?.held) this.session.unhold();
	}
}

export const telefono = new Telefono();
